"use client";

/**
 * เติมเงินทรูมันนี่ด้วยสลิป — ลูกค้าโอนเข้าวอลเล็ทร้าน แล้วอัพโหลดสลิปตรงนี้
 *
 * อ่าน QR จากรูปฝั่ง browser (jsQR โหลดจาก CDN — ไม่เพิ่ม dependency)
 * รูปไม่ถูกอัพขึ้น server ส่งเฉพาะค่า QR ไปตรวจ แล้ว poll สถานะจนแอดมินยืนยัน
 *
 * ลำดับการใช้งาน: กรอกยอด → เปิดใบแจ้งโอน (intent) → สแกน QR/โอนตามยอดเป๊ะ
 * → miniapp เห็นเงินเข้าตรงยอดแล้วตัดเครดิตเอง ไม่ต้องอัพสลิป
 *
 * ยอดที่ต้องโอนคือ `amount_baht` จาก server (มีเศษสตางค์ไม่ซ้ำ) ไม่ใช่ยอดที่ลูกค้ากรอก
 * เศษนี่แหละคือตัวจับคู่ว่าเงินก้อนไหนของใคร — ห้ามปัดเศษทิ้งเด็ดขาด
 *
 * ⚠️ จับคู่ได้ ≠ เครดิตเข้า — miniapp ไม่มีท่อเขียนเครดิต แอดมินยังต้องกดยืนยันในหลังบ้าน
 * ที่ประหยัดไปคือลูกค้าไม่ต้องอัพสลิป และแอดมินไม่ต้องนั่งจับคู่เอง
 * ห้ามเขียนบนจอว่า "เครดิตเข้าเองในไม่กี่วินาที" เด็ดขาด
 *
 * อัพสลิปยังอยู่เป็นทางสำรอง เผื่อโอนผิดยอด/จับคู่อัตโนมัติไม่ติด
 * และถ้าเส้น intent ยังไม่ขึ้น (404/5xx) จะถอยไปใช้อัพสลิปล้วนแบบเดิม
 *
 * ใช้ได้ 2 ที่:
 *   variant="page"     — หน้า /truewallet เดี่ยว ๆ (ค่าเริ่มต้น มีหัวข้อใหญ่)
 *   variant="embedded" — ฝังเป็นแท็บ "ทรูมันนี่" ในหน้าฝาก (ไม่มีหัวข้อ ไม่มี padding นอก)
 *
 * ถ้าฝั่ง server ยังไม่ได้ตั้งคีย์เติมเงิน (`enabled: false`) จะ render `fallback`
 * ที่ส่งเข้ามาแทน — หน้าฝากส่งของเดิม (คัดลอกเบอร์ไปโอนเอง) มาเป็นตัวสำรอง
 */

import React from "react";
import { useP2PStore } from "@/sections/ggez/wallet/p2p/p2pStore";
import { useKbizSessionStore } from "@/stores/kbizSessionStore";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Image from "next/image";
import { useQRCode } from "next-qrcode";

import CopyButton from "@/components/ui/CopyButton";
import useTruewalletTopup from "@/hooks/fetchers/useTruewalletTopup";
import fetcher from "@/libs/fetcher";
import { promptpayPayload } from "@/libs/promptpayQr";

const JSQR_SRC = "https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js";

type JsQrFn = (
  data: Uint8ClampedArray,
  width: number,
  height: number,
) => { data: string } | null;

declare global {
  interface Window {
    jsQR?: JsQrFn;
  }
}

let jsQrPromise: Promise<JsQrFn> | null = null;

function loadJsQr(): Promise<JsQrFn> {
  if (window.jsQR) return Promise.resolve(window.jsQR);
  if (jsQrPromise) return jsQrPromise;
  jsQrPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = JSQR_SRC;
    script.onload = () =>
      window.jsQR ? resolve(window.jsQR) : reject(new Error("jsQR not loaded"));
    script.onerror = () => reject(new Error("โหลดตัวอ่าน QR ไม่สำเร็จ"));
    document.head.appendChild(script);
  });
  return jsQrPromise;
}

async function decodeQrFromFile(file: File): Promise<string | null> {
  const jsQR = await loadJsQr();
  const bitmap = await createImageBitmap(file);
  // ลองขนาดจริงก่อน แล้วขยาย 2 เท่าเผื่อรูปเล็ก
  for (const scale of [1, 2]) {
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width * scale;
    canvas.height = bitmap.height * scale;
    const ctx = canvas.getContext("2d");
    if (!ctx) continue;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const { data, width, height } = ctx.getImageData(
      0,
      0,
      canvas.width,
      canvas.height,
    );
    const code = jsQR(data, width, height);
    if (code?.data) return code.data;
  }
  return null;
}

type TopupRequestRow = {
  id: string;
  status: "pending" | "verified" | "approved" | "rejected";
  amount: number | null;
};

type TopupIntent = {
  intent_id: string;
  /** สตางค์ */
  amount: number;
  /** ยอดที่ต้องโอนเป๊ะ เช่น "300.37" */
  amount_baht: string;
  /** deep link ต่อรายการของทรู — null ได้เมื่อทรูล่ม/ติดโควตา */
  pay_url: string | null;
  wallet_mobile: string;
  expires_at: string;
  status?: string;
  /** truemoney = โอนในแอปทรู · bank = โอนจากแอปธนาคารผ่านพร้อมเพย์ */
  channel: DepositChannel;
};

type DepositChannel = "truemoney" | "bank";

/**
 * แถวที่ GET /intents คืนมา — ชื่อฟิลด์ไม่เหมือนตอน POST สร้าง (ยืนยันจาก response จริง)
 *   POST คืน  intent_id / amount(สตางค์) / amount_baht / pay_url / wallet_mobile
 *   GET  คืน  id        / amount(สตางค์)   ... ไม่มี amount_baht, pay_url, wallet_mobile
 * ถ้าไม่แปลงให้เป็นรูปเดียวกัน จะเจอ 2 อาการ:
 *   1) ยอดที่ต้องโอนโชว์เป็นค่าว่าง (ไม่มี amount_baht)
 *   2) poll ไม่มีวันเจอใบตัวเอง เพราะไปเทียบ intent_id กับแถวที่มีแต่ id
 */
type RawIntent = {
  intent_id?: string | number;
  id?: string | number;
  amount?: number | string;
  amount_baht?: string;
  pay_url?: string | null;
  wallet_mobile?: string;
  expires_at: string;
  status?: string;
  channel?: string;
};

const normalizeIntent = (row: RawIntent): TopupIntent => {
  const satang = Number(row.amount ?? 0);
  return {
    intent_id: String(row.intent_id ?? row.id ?? ""),
    amount: satang,
    amount_baht: row.amount_baht ?? (satang / 100).toFixed(2),
    pay_url: row.pay_url ?? null,
    wallet_mobile: row.wallet_mobile ?? "",
    expires_at: row.expires_at,
    status: row.status,
    channel: row.channel === "bank" ? "bank" : "truemoney",
  };
};

/** สลิป (ทางสำรอง) */
type SlipPhase =
  | { name: "idle" }
  | { name: "reading" }
  | { name: "submitting" }
  | { name: "waiting"; requestId: string; amountBaht: string | null }
  | { name: "error"; message: string };

/** พร้อมเพย์ของวอลเล็ททรู = "14000" + เบอร์ 10 หลัก */
function walletPromptpayId(mobile: string | null | undefined): string {
  const digits = (mobile ?? "").replace(/\D/g, "");
  return /^0\d{9}$/.test(digits) ? `14000${digits}` : "";
}

/** ยอดกดเร็ว — กดแล้ว "บวกเพิ่ม" เข้ายอดเดิม กด 100 สองครั้ง = 200 */
import { MIN_PROMPTPAY_DEPOSIT } from "@/libs/depositLimits";
import type { DepositAmountBounds } from "@/libs/depositChannelRules";
import { flexGapFallbackSx } from "@/libs/oldSafari";
/**
 * ดันยอดกดเร็วให้ไม่ต่ำกว่าขั้นต่ำ — ตัวแรกกลายเป็น "ขั้นต่ำพอดี"
 * ไม่งั้นลูกค้ากด +100 ตอนขั้นต่ำ 200 แล้วปุ่มฝากยังกดไม่ได้ งงว่าทำอะไรผิด
 */
function quickAmountsFor(base: readonly number[], min: number): number[] {
  if (base[0] >= min) return [...base];
  return [min, ...base.filter((amt) => amt > min)];
}

const QUICK_AMOUNTS = [50, 100, 300, 500];

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_TRIES = 600; // ~30 นาที เท่าอายุใบแจ้งโอน

type TruewalletTopupProps = {
  /** "page" = หน้าเดี่ยว (ค่าเริ่มต้น) · "embedded" = ฝังในแท็บหน้าฝาก */
  variant?: "page" | "embedded";
  /** ใช้แทนเมื่อ server ยังไม่ได้เปิดระบบเติมอัตโนมัติ */
  fallback?: React.ReactNode;
  /** อาร์ตหัวการ์ดของแบรนด์ (ไม่ส่ง = ไม่โชว์รูป) */
  heroImage?: string;
  /** ยอดฝากขั้นต่ำที่ตรวจฝั่งหน้าเว็บ (ฝั่ง miniapp ไม่ได้บังคับขั้นต่ำไว้) */
  minAmount?: number;
  /**
   * วงเงินจาก config ของหลังบ้าน
   * ส่งมาแล้วใช้ค่านี้อย่างเดียว ไม่ยกพื้นพร้อมเพย์ 200 ทับ
   */
  amountBounds?: DepositAmountBounds | null;
  /** ช่องทางตั้งต้น — ส่ง "bank" มาเมื่อคนเลือกจากแท็บข้างนอกแล้ว */
  defaultChannel?: DepositChannel;
  /**
   * โชว์ปุ่มเลือกช่องทางในตัวไหม
   *
   * หน้าฝากของเฮงแยกช่องทางไว้ที่ rail ข้างนอกแล้ว ถ้าโชว์ปุ่มในนี้อีกจะกลายเป็น
   * เลือกช่องทางซ้อนสองชั้น — ซึ่งเจ้าของงานตีกลับมาแล้วรอบหนึ่ง
   */
  showChannelPicker?: boolean;
  /** ยอดตั้งต้น (บาท) — ฝากด่วนกดยอดมาแล้ว ไม่ต้องให้ลูกค้ากรอกซ้ำ */
  initialAmount?: number;
  /** ฝากด่วน: กดยอด + เลือกช่องมาแล้ว → เปิดใบให้เลย ไม่ต้องกดสร้างซ้ำ */
  autoSubmit?: boolean;
};

export default function TruewalletTopup({
  variant = "page",
  fallback = null,
  heroImage,
  minAmount: minAmountProp = 1,
  amountBounds = null,
  defaultChannel = "truemoney",
  showChannelPicker = true,
  initialAmount,
  autoSubmit = false,
}: TruewalletTopupProps) {
  const { enabled, walletMobile, bankEnabled, promptpayId, isLoading } =
    useTruewalletTopup();
  const [channel, setChannel] = React.useState<DepositChannel>(defaultChannel);
  const { Image: QRImage } = useQRCode();
  const embedded = variant === "embedded";

  const [amountInput, setAmountInput] = React.useState(
    initialAmount && initialAmount > 0 ? String(initialAmount) : "",
  );
  const [formError, setFormError] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [intent, setIntent] = React.useState<TopupIntent | null>(null);
  /** เส้น intent ยังไม่ขึ้นบน server ตัวนี้ → ถอยไปอัพสลิปล้วน */
  const [intentUnavailable, setIntentUnavailable] = React.useState(false);
  const [matched, setMatched] = React.useState(false);
  const [credited, setCredited] = React.useState(false);
  const [expired, setExpired] = React.useState(false);

  /* กางใบอยู่ = กระเป๋าเงินพับยอดเงิน/แท็บวิธีฝากให้ ใบจะได้จบในจอเดียวบนมือถือ
     (ลูกค้าหาปุ่มล่างไม่เจอแล้วเงินไม่เข้า — เคสที่แจ้งเข้ามาซ้ำ ๆ) */
  const setTicketOpen = useKbizSessionStore((state) => state.setTicketOpen);
  React.useEffect(() => {
    setTicketOpen(Boolean(intent));
  }, [intent, setTicketOpen]);
  /* ล้างธงตอนถอดจอทิ้งเท่านั้น — รวมไว้ใน effect ข้างบนไม่ได้ เพราะ cleanup
     จะยิงทุกครั้งที่ใบถูกแทนด้วยก้อนใหม่ (ตอน poll สถานะ) แล้วธงจะกะพริบเป็น false
     ทำให้จอเด้งกลับหน้ากรอกยอดทั้งที่ใบยังอยู่ */
  React.useEffect(() => () => setTicketOpen(false), [setTicketOpen]);

  /* พร้อมเพย์ (ช่อง bank = QR เข้าวอลเล็ท) โดนค่าธรรมเนียมรายรายการ
     → ยกพื้นเป็น MIN_PROMPTPAY_DEPOSIT เมื่อหน้าฝากไม่ได้ส่งวงเงินจาก config มา
     มี amountBounds แล้ว ใช้ขั้นต่ำ/สูงสุดจากหลังบ้านอย่างเดียว */
  const minAmount = amountBounds
    ? (amountBounds.min ?? minAmountProp)
    : channel === "bank"
      ? Math.max(minAmountProp, MIN_PROMPTPAY_DEPOSIT)
      : minAmountProp;
  const maxAmount = amountBounds?.max ?? null;
  const amountValue = Number(amountInput);
  /** ยอดอยู่นอกวงเงิน = กดปุ่มฝากไม่ได้ (ไม่ใช่กดแล้วค่อยขึ้น error) */
  const amountBelowMin =
    !amountInput.trim() ||
    !Number.isFinite(amountValue) ||
    amountValue < minAmount ||
    (maxAmount != null && amountValue > maxAmount);

  const [slip, setSlip] = React.useState<SlipPhase>({ name: "idle" });
  const [secondsLeft, setSecondsLeft] = React.useState(0);

  const [restoring, setRestoring] = React.useState(true);
  React.useEffect(() => {
    setChannel(defaultChannel);
    setIntent((current) =>
      current && current.channel !== defaultChannel ? null : current,
    );
    setIntentUnavailable(false);
    setFormError("");
  }, [defaultChannel]);
  const pollTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearPoll = () => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    pollTimer.current = null;
  };
  React.useEffect(() => clearPoll, []);

  /* --------------------- กู้ใบที่เปิดค้างไว้ตอนเข้าหน้ามาใหม่ ---------------------
     ใบอยู่ฝั่ง server 30 นาที ถ้าไม่ดึงกลับมา ลูกค้ารีเฟรชแล้วจะไม่รู้ว่าต้องโอนยอดไหน
     แล้วไปเปิดใบใหม่ทับ ทั้งที่ใบเดิมยังจองยอดค้างอยู่ */
  React.useEffect(() => {
    if (!enabled) {
      setRestoring(false);
      return;
    }
    let cancelled = false;
    // กันจอค้างเป็นวงกลมหมุนถาวรถ้า request ไม่ตอบกลับ — ยอมเริ่มที่ฟอร์มดีกว่าจอเปล่า
    const safety = setTimeout(() => {
      if (!cancelled) setRestoring(false);
    }, 8000);
    (async () => {
      try {
        // เส้น open คืน wallet_mobile ของใบมาด้วย (ลิสต์ไม่คืน) — จำเป็นตอนหลังบ้านสลับบัญชี
        const res = await fetcher<{ data?: RawIntent[] }>(
          "/api/topup-truewallet/intents/open",
        );
        if (cancelled) return;
        const open = (res.data ?? [])
          .map(normalizeIntent)
          .filter(
            (row) =>
              row.status === "open" &&
              row.channel === defaultChannel &&
              new Date(row.expires_at).getTime() > Date.now(),
          )
          .sort(
            (a, b) =>
              new Date(b.expires_at).getTime() - new Date(a.expires_at).getTime(),
          )[0];
        if (open) {
          setIntent(open);
          pollIntent(open.intent_id, 0);
        } else {
          setIntent((current) =>
            current && current.channel !== defaultChannel ? null : current,
          );
        }
      } catch {
        // ดึงไม่ได้ก็เริ่มที่หน้ากรอกยอดตามปกติ
      } finally {
        if (!cancelled) setRestoring(false);
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(safety);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, defaultChannel]);

  /* ------------------------- นาฬิกาถอยหลังของใบแจ้งโอน ------------------------- */
  React.useEffect(() => {
    if (!intent || matched) return;
    const tick = () => {
      const left = Math.max(
        0,
        Math.floor((new Date(intent.expires_at).getTime() - Date.now()) / 1000),
      );
      setSecondsLeft(left);
      if (left === 0) {
        setExpired(true);
        clearPoll();
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [intent, matched]);

  /* ---------------------------- poll จนกว่าจะจับคู่ได้ ---------------------------- */
  const pollIntent = React.useCallback((intentId: string, tries: number) => {
    if (tries >= POLL_MAX_TRIES) return;
    pollTimer.current = setTimeout(async () => {
      try {
        const res = await fetcher<{ data?: RawIntent[] }>(
          "/api/topup-truewallet/intents",
        );
        const row = (res.data ?? [])
          .map(normalizeIntent)
          .find((r) => r.intent_id === String(intentId));
        if (row?.status === "matched") {
          setMatched(true);
          pollCredit(0);
          return;
        }
        if (row?.status === "expired" || row?.status === "cancelled") {
          setExpired(true);
          return;
        }
      } catch {
        // เน็ตสะดุดชั่วคราว — วนต่อ
      }
      pollIntent(intentId, tries + 1);
    }, POLL_INTERVAL_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** จับคู่ได้แล้ว — ตามต่อว่าเครดิตเข้าจริงหรือยัง */
  const pollCredit = React.useCallback((tries: number) => {
    if (tries >= 40) return; // ~2 นาที แล้วปล่อยให้ข้อความ "กำลังเติมเครดิต" ค้างไว้
    pollTimer.current = setTimeout(async () => {
      try {
        const res = await fetcher<{ data?: TopupRequestRow[] }>(
          "/api/topup-truewallet",
        );
        const done = res.data?.find((r) => r.status === "approved");
        if (done) {
          setCredited(true);
          return;
        }
      } catch {
        // วนต่อ
      }
      pollCredit(tries + 1);
    }, POLL_INTERVAL_MS);
  }, []);

  /* ------------------------------ เปิดใบแจ้งโอน ------------------------------ */
  const handleCreateIntent = async () => {
    /* doubleSubmitGuard — ปุ่มมี disabled แล้ว แต่กด Enter ในช่องยอดเรียกตรงนี้ได้
       กดรัว ๆ ก่อน server ตอบ = เปิดใบซ้ำหลายใบ ลูกค้าสับสนว่าต้องโอนยอดไหน */
    if (creating) return;
    const value = Number(amountInput);
    if (!amountInput.trim() || Number.isNaN(value)) {
      setFormError("กรุณากรอกจำนวนเงิน");
      return;
    }
    if (value < minAmount) {
      setFormError(`ฝากขั้นต่ำ ${minAmount} บาท`);
      return;
    }
    if (maxAmount != null && value > maxAmount) {
      setFormError("ยอดเกินที่ช่องนี้รับได้");
      return;
    }
    setFormError("");
    setCreating(true);
    try {
      const res = await fetcher<{ data: RawIntent }>(
        "/api/topup-truewallet/intents",
        {
          method: "post",
          body: bankEnabled && channel === "bank" ? { amount: value, channel } : { amount: value },
        },
      );
      const created = normalizeIntent(res.data);
      setIntent(created);
      setExpired(false);
      setMatched(false);
      setCredited(false);
      pollIntent(created.intent_id, 0);
    } catch (error) {
      const err = error as {
        response?: { status?: number; data?: { message?: string } };
      };
      const status = err.response?.status;
      if (status === 409) {
        setFormError(
          "ยอดนี้มีคนจองอยู่พอดี ลองเปลี่ยนยอดอีกนิด — ถ้าเพิ่งกดยกเลิกไป รอสักครู่แล้วลองใหม่",
        );
      } else if (status === 403) {
        setFormError("บัญชีนี้ใช้ช่องทางนี้ไม่ได้ กรุณาติดต่อแอดมิน");
      } else if (status === 401) {
        setFormError("กรุณาเข้าสู่ระบบก่อนเติมเงิน");
      } else if (status === 404 || (status ?? 0) >= 500) {
        // เส้นยังไม่ขึ้น/ล่ม → ถอยไปทางอัพสลิป ลูกค้ายังฝากได้
        setIntentUnavailable(true);
      } else {
        setFormError(
          err.response?.data?.message ?? "เปิดรายการไม่สำเร็จ ลองใหม่อีกครั้ง",
        );
      }
    } finally {
      setCreating(false);
    }
  };

  /* ฝากด่วน — รับยอดมาจากหน้าเลือกช่องทางแล้ว เปิดใบให้เลยครั้งเดียว
     ไม่งั้นลูกค้าต้องพิมพ์ยอดซ้ำอีกรอบ (เจ้าของงานทัก 6 ต.ค.)
     เว็บกลุ่มนี้รับ prop autoSubmit ไว้แต่ไม่เคยมีโค้ดที่ใช้มันเลย
     ยกชุดเดียวกับที่ heng36/ตองเก้าใช้อยู่มาใส่ให้ตรงกัน */
  const autoSubmitted = React.useRef(false);
  React.useEffect(() => {
    if (!autoSubmit || autoSubmitted.current) return;
    if (isLoading || restoring || !enabled || intent) return;
    // แท็บพร้อมเพย์ต้องรอให้ช่องพร้อมเพย์เปิดจริง ไม่งั้นได้ใบทรูมันนี่ผิดช่อง
    if (defaultChannel === "bank" && !bankEnabled) return;
    if (!(initialAmount && initialAmount >= minAmount)) return;
    if (maxAmount != null && initialAmount > maxAmount) return;
    autoSubmitted.current = true;
    void handleCreateIntent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSubmit, isLoading, restoring, enabled, intent, bankEnabled]);

  /* ยังไม่มีใบ + ฝากด่วนส่งยอดมาครบ = กำลังจะเปิดใบให้เอง ไม่ต้องโชว์ฟอร์ม */
  const autoOpenCurtain =
    autoSubmit &&
    !intent &&
    !intentUnavailable &&
    !formError &&
    Boolean(initialAmount && initialAmount >= minAmount);

  const [cancelling, setCancelling] = React.useState(false);
  /* cancelConfirm — ลูกค้าที่โอนเงินไปแล้วเผลอกดยกเลิก = ใบปิดทันที เงินค้าง แอดมินต้องตามแก้
     จอจับคู่ (P2P) มีด่านถามอยู่แล้ว แต่ทรู/พร้อมเพย์ยังไม่มี (รีวิวมุมความเสี่ยงเงิน 6 ต.ค.) */
  const [confirmCancel, setConfirmCancel] = React.useState(false);

  /**
   * ล้างจอกลับไปหน้ากรอกยอด + ปิดใบจริงที่ server
   *
   * ต้องปิดที่ server ด้วย ไม่งั้นใบยังสถานะ open ค้างยอดไว้
   * แล้วพอสลับแท็บ/รีเฟรช ตัวกู้ใบจะดึงกลับมาโชว์อีก = กดยกเลิกแล้วไม่หาย
   */
  const resetToForm = async () => {
    clearPoll();
    const openId = intent && !matched && !credited ? intent.intent_id : "";
    setIntent(null);
    setExpired(false);
    setMatched(false);
    setCredited(false);
    setSlip({ name: "idle" });
    if (!openId) return;
    setCancelling(true);
    try {
      // path ต้องเป็นแบบคงที่ล้วน ห้ามใส่ id ลงใน path — rewrite `/api/:path*`
      // ใน next.config ถูกเช็คก่อนเส้นทาง dynamic เส้นที่มี [id] จะโดนกินไปที่ BO
      // แล้วตอบ 404 ทำให้ปุ่มยกเลิกกลายเป็นปุ่มหลอก (ดูโน้ตในไฟล์ route ของเส้นนี้)
      await fetcher("/api/topup-truewallet/intents/cancel", {
        method: "post",
        body: { id: openId },
      });
    } catch {
      // ปิดไม่สำเร็จ (ใบหมดอายุ/เน็ตสะดุด) — ใบจะหมดอายุเองใน 30 นาที
      // จอกลับไปหน้ากรอกยอดแล้ว ไม่ต้องรบกวนลูกค้าด้วย error นี้
    } finally {
      setCancelling(false);
    }
  };

  /* ------------------------------ ทางสำรอง: สลิป ------------------------------ */
  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // เลือกไฟล์เดิมซ้ำได้
    if (!file) return;

    setSlip({ name: "reading" });
    let qrValue: string | null = null;
    try {
      qrValue = await decodeQrFromFile(file);
    } catch {
      setSlip({ name: "error", message: "อ่านรูปไม่สำเร็จ ลองใหม่อีกครั้ง" });
      return;
    }
    if (!qrValue) {
      setSlip({
        name: "error",
        message:
          "อ่าน QR จากสลิปไม่ได้ กรุณาใช้รูปสลิปจากแอปทรูมันนี่ที่ชัดเจน (ไม่ใช่สลิปธนาคาร)",
      });
      return;
    }

    setSlip({ name: "submitting" });
    try {
      const res = await fetcher<{
        data?: { request_id: string; amount_baht: string };
      }>("/api/topup-truewallet", { method: "post", body: { qr_value: qrValue } });
      setSlip({
        name: "waiting",
        requestId: res.data?.request_id ?? "",
        amountBaht: res.data?.amount_baht ?? null,
      });
      setMatched(true);
      pollCredit(0);
    } catch (error) {
      const err = error as {
        response?: { status?: number; data?: { status?: string; message?: string } };
      };
      const code = err.response?.data?.status;
      if (code === "invalid_qr") {
        setSlip({
          name: "error",
          message:
            "สลิปนี้ไม่ใช่สลิปทรูมันนี่ กรุณาโอนแบบวอลเล็ท → วอลเล็ท แล้วใช้สลิปจากแอปทรูมันนี่",
        });
      } else if (code === "duplicate") {
        setSlip({ name: "error", message: "สลิปใบนี้ถูกใช้เติมไปแล้ว ใช้ซ้ำไม่ได้ครับ" });
      } else if (err.response?.status === 401) {
        setSlip({ name: "error", message: "กรุณาเข้าสู่ระบบก่อนเติมเงิน" });
      } else {
        setSlip({
          name: "error",
          message:
            err.response?.data?.message ?? "ส่งสลิปไม่สำเร็จ ลองใหม่อีกครั้ง",
        });
      }
    }
  };

  const slipBusy = slip.name === "reading" || slip.name === "submitting";

  /* --------------------------------- render --------------------------------- */


  // ยังไม่รู้ผล — อย่าเพิ่งวูบไปเป็นตัวสำรอง เดี๋ยวจอกระพริบ
  if (isLoading || (enabled && restoring)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4, width: "100%" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  // ระบบตรวจสลิปยังไม่พร้อม → ถอยไปใช้ของเดิมที่หน้าฝากส่งมา
  if (!enabled) {
    return <>{fallback}</>;
  }

  /* promptpayPanelPad — การ์ดในจอฝากบีบขอบบนมือถือ ให้ทั้งจอจบในหน้าเดียว
     (ลูกค้าบางเครื่องเลื่อนไม่เจอปุ่มล่าง เลยไม่ได้ทำรายการต่อ) */
  const panelSx = { p: { xs: 1.25, sm: 2 }, borderRadius: 2, bgcolor: "rgba(255,255,255,0.06)" } as const;
  const mmss = `${String(Math.floor(secondsLeft / 60)).padStart(2, "0")}:${String(
    secondsLeft % 60,
  ).padStart(2, "0")}`;

  const slipUploadButton = (
    <>
      <Button
        component="label"
        variant="outlined"
        size="large"
        fullWidth
        disabled={slipBusy}
        startIcon={
          slipBusy ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />
        }
      >
        {slip.name === "reading"
          ? "กำลังอ่านสลิป..."
          : slip.name === "submitting"
            ? "กำลังตรวจสอบ..."
            : "อัพโหลดสลิป"}
        <input type="file" accept="image/*" hidden onChange={handleFile} />
      </Button>
      <Typography variant="caption" sx={{ display: "block", mt: 1, opacity: 0.6 }}>
        ใช้รูปสลิปจากแอปทรูมันนี่เท่านั้น · สลิปหนึ่งใบเติมได้ครั้งเดียว
      </Typography>
      {slip.name === "error" && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {slip.message}
        </Alert>
      )}
    </>
  );

  // QR พร้อมเพย์ต้องชี้วอลเล็ทที่ miniapp ผูกกับใบนี้ (แยกตามเว็บ เปลี่ยนได้จากหลังบ้าน)
  // ไม่ใช่เบอร์ที่หน้าเว็บจำไว้ — ใบเก่าที่ไม่มีเบอร์ติดมาค่อยใช้เบอร์ปัจจุบันของเว็บ
  const bankTarget = walletPromptpayId(intent?.wallet_mobile) || promptpayId;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        /* ticketGapTight — ช่องไฟระหว่างบล็อกบนมือถือ 16px × 6 บล็อก = เกือบ 100px
           ย่อเหลือ 8px เพื่อแลกกับ QR ที่ใหญ่ขึ้น ใบยังจบในจอเดียวเหมือนเดิม */
        gap: { xs: 1, sm: 2 },
        p: embedded ? 0 : 2,
        width: "100%",
        mt: embedded ? 1.5 : 0,
      }}
    >
      {heroImage && !intent && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Image
            src={heroImage}
            width={200}
            height={200}
            alt="ฝากผ่านทรูมันนี่ วอลเล็ท"
            style={{ width: 200, height: "auto" }}
          />
        </Box>
      )}

      {/* promptpayHideDupTitle — หลังออกใบแล้ว หัวข้อนี้พูดซ้ำกับแถบช่องทางด้านบน
          ("พร้อมเพย์ · สแกนพร้อมเพย์ด้วยแอปธนาคาร") บนมือถือจึงซ่อนทิ้ง
          เอาที่ว่างไปให้ QR กับปุ่มล่างอยู่ในจอเดียวกัน ตอนยังไม่ออกใบยังโชว์ปกติ */}
      <Box sx={{ textAlign: "center", display: intent ? { xs: "none", sm: "block" } : "block" }}>
        <Typography sx={{ fontWeight: 900, fontSize: { xs: 19, md: 21 }, lineHeight: 1.3 }}>
          {(intent?.channel ?? channel) === "bank"
            ? "ฝากพร้อมเพย์ — โอนจากแอปธนาคาร"
            : "ฝากผ่านทรูมันนี่ วอลเล็ท"}
        </Typography>
        <Typography sx={{ opacity: 0.75, fontSize: { xs: 13, md: 14 }, mt: 0.5 }}>
          {!intent
            ? channel === "bank"
              ? "กรอกจำนวนเงิน แล้วโอนจากแอปธนาคารตามยอดเศษสตางค์ที่ระบบแจ้ง"
              : "กรอกจำนวนเงิน แล้วโอนไปยังบัญชีทรูมันนี่ที่ระบบแจ้ง"
            : intent.channel === "bank"
              ? "โอนจากแอปธนาคารให้ตรงยอดที่ระบบแจ้ง เครดิตเข้าอัตโนมัติ"
              : "โอนให้ตรงยอดที่ระบบแจ้ง เครดิตเข้าอัตโนมัติ"}
        </Typography>
      </Box>

      {/* ---------- เครดิตเข้าแล้ว ---------- */}
      {credited ? (
        <>
          <Alert severity="success">
            🎉 เติมเครดิต{intent ? ` ${intent.amount_baht} บาท` : ""} เรียบร้อยแล้ว
            ขอบคุณ
          </Alert>
          <Button variant="outlined" fullWidth onClick={() => setConfirmCancel(true)}>
            ฝากอีกครั้ง
          </Button>
        </>
      ) : matched ? (
        <Alert severity="info" icon={<CircularProgress size={18} />}>
          ตรวจพบเงินเข้าแล้ว ✓ ระบบกำลังเติมเครดิตให้
          ไม่ต้องส่งสลิปซ้ำ และปิดหน้านี้ได้เลย
        </Alert>
      ) : autoOpenCurtain ? (
        /* autoOpenCurtain — ฝากด่วนส่งยอดมาแล้ว ระบบกำลังเปิดใบให้เอง
           ห้ามโชว์ฟอร์มกรอกยอดแว็บ ๆ ระหว่างรอ (เจ้าของงานทัก 6 ต.ค.)
           ลูกค้าเห็นช่องกรอกยอดแล้วนึกว่าต้องกรอกเอง */
        <Box sx={{ ...panelSx, alignItems: "center", display: "flex", gap: 1.25, justifyContent: "center", py: 3 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" sx={{ opacity: 0.85, fontWeight: 700 }}>
            กำลังเปิดรายการฝาก...
          </Typography>
        </Box>
      ) : intentUnavailable || !intent ? (
        /* ---------- ขั้นที่ 1: กรอกยอด ---------- */
        <Box sx={panelSx}>
          {intentUnavailable ? (
            /* เส้นจองยอดใช้ไม่ได้ → โอนเองแล้วอัพสลิป */
            <>
              <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                1. โอนผ่านแอปทรูมันนี่ (วอลเล็ท → วอลเล็ท) มาที่เบอร์
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Typography variant="h5" fontWeight={700} letterSpacing={1}>
                  {walletMobile}
                </Typography>
                <CopyButton text={walletMobile} />
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.8, mb: 1.5 }}>
                2. โอนเสร็จแล้วอัพโหลดสลิป ระบบตรวจและเติมเครดิตให้อัตโนมัติ
              </Typography>
              {slipUploadButton}
            </>
          ) : (
            <>
              {bankEnabled && showChannelPicker && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 0.75,
                    mb: 1.5,
                  }}
                >
                  {(
                    [
                      ["truemoney", "แอปทรูมันนี่"],
                      ["bank", "โอนจากธนาคาร"],
                    ] as const
                  ).map(([value, label]) => (
                    <Button
                      key={value}
                      variant={channel === value ? "contained" : "outlined"}
                      onClick={() => setChannel(value)}
                      sx={{ minHeight: 42, fontWeight: 800 }}
                    >
                      {label}
                    </Button>
                  ))}
                </Box>
              )}
              <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                จำนวนเงินฝาก
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={amountInput}
                onChange={(e) => {
                  /* intOnlyAmount — ช่องอื่นกรองเป็นจำนวนเต็มหมดแล้ว ช่องนี้ยังพิมพ์ทศนิยมได้
                     ซึ่งไปชนกับเศษสตางค์ที่ระบบเติมให้เพื่อจับคู่ยอด (รีวิว 6 ต.ค.) */
                  setAmountInput(e.target.value.replace(/[^0-9]/g, ""));
                  if (formError) setFormError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCreateIntent();
                  }
                }}
                placeholder={`ขั้นต่ำ ${minAmount} บาท`}
                error={Boolean(formError)}
                helperText={formError || " "}
                inputProps={{ inputMode: "numeric", min: minAmount }}
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: 0.75,
                }}
              >
                {quickAmountsFor(QUICK_AMOUNTS, minAmount).map((amt) => (
                  <Button
                    key={amt}
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      // บวกทบเข้ายอดเดิม ไม่ใช่แทนที่ — กด 100 ซ้ำ = 200
                      const current = Number(amountInput) || 0;
                      setAmountInput(String(current + amt));
                      setFormError("");
                    }}
                    /* touchTarget — ปุ่มเติมยอดเร็ว ต่ำกว่า 40px นิ้วแตะพลาดง่ายบนมือถือ */
                    sx={{ minHeight: 40, fontWeight: 800, px: 0.5 }}
                  >
                    +{amt.toLocaleString("th-TH")}
                  </Button>
                ))}
              </Box>
              {/* กดบวกอย่างเดียวแล้วถอยไม่ได้ ต้องมีทางล้าง */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                <Button
                  size="small"
                  color="inherit"
                  disabled={!amountInput}
                  onClick={() => {
                    setAmountInput("");
                    setFormError("");
                  }}
                  sx={{ opacity: 0.7, fontSize: 12 }}
                >
                  ล้างยอด
                </Button>
              </Box>
              <Button
                type="button"
                variant="contained"
                size="large"
                fullWidth
                disabled={creating || amountBelowMin}
                onClick={handleCreateIntent}
                startIcon={creating ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {creating ? "กำลังเปิดรายการ..." : "สร้างรายการฝาก"}
              </Button>
              <Typography
                align="center"
                sx={{ display: "block", mt: 1, opacity: 0.6, fontSize: 12 }}
              >
                ยอดเงินเข้าระบบอัตโนมัติหลังตรวจสอบสำเร็จ
              </Typography>
            </>
          )}
        </Box>
      ) : expired ? (
        /* ---------- ใบหมดอายุ ---------- */
        <>
          <Alert severity="warning">
            หมดเวลารอเงินเข้าอัตโนมัติแล้ว
          </Alert>
          {/* โอนไปแล้วแต่ระบบยังไม่เห็น — แนบสลิปเป็นทางเร่ง ไม่ต้องรอแอดมินไล่ดู */}
          <Box sx={panelSx}>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 0.5, fontWeight: 800 }}>
              โอนไปแล้ว? แนบสลิปเพื่อเร่งยอดฝาก
            </Typography>
            <Typography variant="caption" sx={{ display: "block", mb: 1.5, opacity: 0.7 }}>
              ระบบจะตรวจสลิปให้ทันที ไม่ต้องรอรอบตรวจอัตโนมัติ
            </Typography>
            {slipUploadButton}
          </Box>
          <Button variant="outlined" fullWidth onClick={() => setConfirmCancel(true)}>
            ยังไม่ได้โอน — สร้างรายการใหม่
          </Button>
        </>
      ) : (
        /* ---------- ขั้นที่ 2: โอนตามยอด ---------- */
        <>
          <Box sx={{ ...panelSx, p: { xs: 1.25, sm: 2 } }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: { xs: 0.5, sm: 1 } }}
            >
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                ยอดที่ต้องโอน (ห้ามแก้ตัวเลข)
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                เหลือเวลา {mmss}
              </Typography>
            </Stack>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h4" fontWeight={900} letterSpacing={0.5}>
                {intent.amount_baht}
              </Typography>
              <Typography sx={{ opacity: 0.7 }}>บาท</Typography>
              <CopyButton text={intent.amount_baht} />
            </Box>
            {/* promptpayDedupeNote — ประโยคนี้ซ้ำกับข้อแรกในรายการเตือนใต้ QR
                ตัดออกให้จอมือถือสั้นลง ลูกค้าจะได้ไม่ต้องเลื่อนหาปุ่มล่าง */}
          </Box>

          <Box sx={{ ...panelSx, p: { xs: 1.25, sm: 2 } }}>
            {intent.channel === "bank" ? (
              /* โอนจากแอปธนาคาร — QR พร้อมเพย์ฝังยอดเศษสตางค์ไว้แล้ว */
              <>
                {(() => {
                  const payload = promptpayPayload(bankTarget, intent.amount_baht);
                  return payload ? (
                    <>
                      {/* promptpayTwoCol + promptpayCompact — QR เคยกิน 240px กลางจอ ทำให้ทั้งหน้ายาวเกินจอมือถือ
                          ลูกค้าต้องเลื่อนลงไปหาเลขพร้อมเพย์กับปุ่ม ย่อบนมือถือให้จบในหน้าเดียว */}
                      {/* promptpayHengLayout — ข้อควรระวังเป็นข้อ ๆ แบบจอฝากของ heng36
                          อ่านง่ายกว่าประโยคยาว และวางคู่กับ QR ได้โดยไม่ดันจอให้ยาว */}
                      {/* promptpayHengRow — ซ้ายข้อห้าม ขวา QR แบบจอฝากของ heng36
                          เรียงลงมาทีละอันทำให้จอมือถือยาวเกิน ต้องเลื่อนหาปุ่มอัปสลิป */}
                      <Box sx={{ alignItems: "center", display: "flex", gap: { xs: 1, sm: 2 }, ...flexGapFallbackSx({ xs: 1, sm: 2 }), mb: { xs: 0.75, sm: 1.5 } }}>
                      <Box component="ul" sx={{ opacity: 0.72, fontSize: { xs: "0.68rem", sm: "0.72rem" }, lineHeight: 1.4, listStyle: "none", m: 0, p: 0, flex: 1, minWidth: 0 }}>
                        {[
                          "ห้ามเปลี่ยนยอดเงิน — เศษสตางค์คือตัวจับคู่รายการ",
                          "ใช้บัญชีที่ลงทะเบียนไว้เท่านั้น",
                          "ต้องโอนจากแอปธนาคาร",
                          "สร้างรายการใหม่ทุกครั้ง ห้ามโอนซ้ำ",
                        ].map((rule) => (
                          <Box
                            component="li"
                            key={rule}
                            /* warnWeight — ข้อ "ห้ามเปลี่ยนยอดเงิน" คือข้อที่พลาดแล้วเงินไม่เข้า
                               ต้องเด่นกว่าข้อรอง ไม่ใช่น้ำหนักเท่ากันหมด (รีวิวมุมลูกค้า 6 ต.ค.) */
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              ...flexGapFallbackSx(0.5),
                              ...(rule.includes("ห้ามเปลี่ยนยอดเงิน")
                                ? { color: "#ffd166", fontWeight: 900, opacity: 1 }
                                : null),
                            }}
                          >
                            <span aria-hidden>⚠</span>
                            <span>{rule}</span>
                          </Box>
                        ))}
                      </Box>
                      <Box
                        sx={{
                          /* qrFramed — ใส่กรอบให้ QR เป็นก้อนเดียว ดูเป็นระเบียบขึ้น
                             (เจ้าของงานสั่ง) + ขยายรูปบนมือถือ สแกนง่ายขึ้น */
                          ...panelSx,
                          /* qrFrameLine — ขอบบาง ๆ ให้เห็นว่าเป็นบล็อกของ QR จริง ๆ
                             พื้นโปร่งของการ์ดอย่างเดียวจางเกินจนไม่เห็นกรอบ */
                          border: "1px solid rgba(255,255,255,0.16)",
                          p: { xs: 0.5, sm: 1 },
                          display: "flex",
                          flexShrink: 0,
                          "& img": {
                            borderRadius: 2,
                            background: "#fff",
                            display: "block",
                            /* qrFrameFit2 — 124px คือขนาดที่วัดแล้วใหญ่กว่าเดิม (100) แต่ทั้งใบยังจบในจอเดียว */
                            height: { xs: 120, sm: 240 },
                            width: { xs: 120, sm: 240 },
                          },
                        }}
                      >
                        <QRImage
                          text={payload}
                          options={{
                            type: "image/jpeg",
                            quality: 1,
                            errorCorrectionLevel: "M",
                            margin: 3,
                            scale: 4,
                            width: 240,
                          }}
                        />
                      </Box>
                      </Box>
                    </>
                  ) : null;
                })()}
                <Typography variant="body2" sx={{ opacity: 0.8, mb: { xs: 0.25, sm: 1 }, fontSize: { xs: 12, sm: "inherit" } }}>
                  หรือเลือก &quot;โอนพร้อมเพย์&quot; ในแอปธนาคารไปที่
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {/* truemoneyFit3 */}
                  {/* targetBigger — เลขปลายทางคือสิ่งที่ลูกค้าต้องเทียบก่อนโอน
                      ของเดิมเล็กกว่ายอดเงินมาก ลูกค้ารีบโอนโดยไม่เช็ค (รีวิวมุมลูกค้า 6 ต.ค.) */}
                  <Typography variant="h5" fontWeight={900} letterSpacing={1} sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}>
                    {bankTarget}
                  </Typography>
                  <CopyButton text={bankTarget} />
                </Box>
                <Typography variant="caption" sx={{ display: "block", mt: { xs: 0.25, sm: 0.5 }, opacity: 0.65, fontSize: { xs: 10.5, sm: "inherit" }, lineHeight: 1.35 }}>
                  ก่อนกดยืนยัน ตรวจว่าเลขพร้อมเพย์ปลายทางตรงกับที่ระบบแจ้ง
                </Typography>
              </>
            ) : intent.pay_url ? (
              <>
                {/* truemoneyHengRow — ซ้ายข้อห้าม ขวา QR แบบจอฝากของ heng36
                    เรียงลงมาทีละอันทำให้จอทรูยาวเกินจอมือถือ ปุ่มเปิดแอปตกขอบ */}
                <Box sx={{ alignItems: "center", display: "flex", gap: { xs: 1, sm: 2 }, ...flexGapFallbackSx({ xs: 1, sm: 2 }), mb: { xs: 0.75, sm: 1.5 } }}>
                  <Box component="ul" sx={{ opacity: 0.72, fontSize: { xs: "0.68rem", sm: "0.72rem" }, lineHeight: 1.4, listStyle: "none", m: 0, p: 0, flex: 1, minWidth: 0 }}>
                    {[
                      "สแกนด้วยแอปทรูมันนี่เท่านั้น — แอปธนาคารสแกนไม่ได้",
                      "ห้ามเปลี่ยนยอดเงิน — เศษสตางค์คือตัวจับคู่รายการ",
                      "หรือกดปุ่มเปิดแอปด้านล่าง",
                      "สร้างรายการใหม่ทุกครั้ง ห้ามโอนซ้ำ",
                    ].map((rule) => (
                      <Box component="li" key={rule} sx={{ display: "flex", gap: 0.5, ...flexGapFallbackSx(0.5) }}>
                        <span aria-hidden>⚠</span>
                        <span>{rule}</span>
                      </Box>
                    ))}
                  </Box>
                {/* truemoneyQrFit — QR ทรูก็ย่อบนมือถือเหมือนช่องอื่น
                    ไม่งั้นปุ่มเปิดแอปกับเบอร์วอลเล็ทตกขอบจอ ลูกค้าเลื่อนไม่เจอ */}
                <Box
                  sx={{
                    /* qrFramed — กรอบเดียวกับ QR พร้อมเพย์ */
                    ...panelSx,
                    /* qrFrameLine */
                    border: "1px solid rgba(255,255,255,0.16)",
                    p: { xs: 0.5, sm: 1 },
                    display: "flex",
                    flexShrink: 0,
                    "& img, & canvas": {
                      borderRadius: 2,
                      background: "#fff",
                      display: "block",
                      /* qrFrameFit */
                      height: { xs: 120, sm: 240 },
                      width: { xs: 120, sm: 240 },
                    },
                  }}
                >
                  <QRImage
                    text={intent.pay_url}
                    options={{
                      type: "image/jpeg",
                      quality: 1,
                      errorCorrectionLevel: "M",
                      margin: 3,
                      scale: 4,
                      width: 240,
                    }}
                  />
                </Box>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  href={intent.pay_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ mt: 1.5 , minHeight: { xs: 40, sm: 48 }, py: { xs: 0.5, sm: 1 } }}
                >
                  เปิดแอปทรูมันนี่
                </Button>
              </>
            ) : (
              /* ทรูล่ม/ติดโควตา — ใบยังจับคู่ได้ แค่ต้องโอนเอง */
              <>
                <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                  โอนผ่านแอปทรูมันนี่ (วอลเล็ท → วอลเล็ท) มาที่เบอร์
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h5" fontWeight={700} letterSpacing={1}>
                    {intent.wallet_mobile || walletMobile}
                  </Typography>
                  <CopyButton text={intent.wallet_mobile || walletMobile} />
                </Box>
              </>
            )}
          </Box>

          {/* promptpayTrim2 — ย่อแถบสถานะบนมือถือ ให้ปุ่มล่างอยู่ในจอเดียวกัน */}
          <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ py: { xs: 0.25, sm: 1 }, "& .MuiAlert-message": { fontSize: { xs: 12, sm: 14 }, lineHeight: { xs: 1.4, sm: 1.5 } } }}>
            รอเงินเข้า — โอนแล้วไม่ต้องอัพสลิป ระบบจับคู่ยอดให้เอง
            แล้วเครดิตจะเข้าหลังแอดมินยืนยัน
          </Alert>

          {/* 3 ข้อนี้คือสาเหตุที่จับคู่ไม่ติดทั้งหมด ต้องบอกก่อนโอน ไม่ใช่หลังพลาด */}
          {/* promptpayMobileFit — บีบกล่องเตือนบนมือถือ เนื้อหาครบเหมือนเดิม
              แต่ไม่ดันปุ่มล่างตกขอบจนลูกค้าหาไม่เจอ */}
          <Box
            component="details"
            open={undefined}
            sx={{
              ...panelSx,
              display: "grid",
              gap: { xs: 0.4, sm: 0.75 },
              p: { xs: 1, sm: 2 },
              "&[open]": { gap: { xs: 0.4, sm: 0.75 } },
              "& > *:not(summary)": { display: { xs: "none", sm: "block" } },
              "&[open] > *": { display: "block" },
            }}
          >
            {/* promptpayTipsCollapse — บนมือถือพับไว้ก่อน กดอ่านได้ ข้อมูลไม่หาย
                ถ้ากางค้างไว้ ปุ่มยกเลิกกับแถบสถานะจะตกขอบจอ ซึ่งเป็นต้นเหตุที่ลูกค้าหาปุ่มไม่เจอ */}
            <Typography
              component="summary"
              sx={{
                fontSize: { xs: 11.5, sm: 12.5 },
                fontWeight: 800,
                opacity: 0.9,
                cursor: "pointer",
                listStyle: { xs: "revert", sm: "none" },
                "&::marker": { color: "currentcolor" },
              }}
            >
              โอนให้ถูกวิธี ไม่งั้นระบบจับคู่ให้ไม่ได้
            </Typography>
            {(intent.channel === "bank"
              ? [
                  "โอนยอดเป๊ะรวมเศษสตางค์ — โอน 300.00 แทน 300.38 จับคู่ไม่ได้",
                  "โอนจากบัญชีธนาคารที่ชื่อตรงกับชื่อที่สมัครไว้เท่านั้น",
                  "ใช้บัญชีที่ลงทะเบียนไว้ ชื่อไม่ตรงระบบตรวจนานขึ้น",
                ]
              : [
                  "โอนยอดเป๊ะรวมเศษสตางค์ — โอน 300.00 แทน 300.38 จับคู่ไม่ได้",
                  bankEnabled
                    ? "ต้องโอนแบบวอลเล็ท → วอลเล็ท (ถ้าโอนจากธนาคาร ให้กดยกเลิกแล้วเลือก \"โอนจากธนาคาร\")"
                    : "ต้องโอนแบบวอลเล็ท → วอลเล็ท เท่านั้น (พร้อมเพย์/เติมผ่านธนาคารใช้ไม่ได้)",
                  "โอนจากเบอร์ที่สมัครไว้เท่านั้น",
                ]
            ).map((line) => (
              <Typography key={line} sx={{ fontSize: { xs: 11, sm: 12 }, opacity: 0.7, lineHeight: { xs: 1.35, sm: 1.5 } }}>
                • {line}
              </Typography>
            ))}
          </Box>

          {/* promptpayGapTrim — ปุ่มล่างต้องอยู่ในจอเดียวกับ QR ไม่ให้ต้องเลื่อนหา */}
          <Button
            size="small"
            onClick={() => setConfirmCancel(true)}
            disabled={cancelling}
            /* redCancel — ยกเลิก = ทิ้งรายการ ต้องอ่านออกว่าเป็นปุ่มอันตราย */
            color="error"
            /* touchTarget — ปุ่มยกเลิกบนใบ เดิม 30px เล็กเกินสำหรับนิ้ว
               ขยับเป็น 36 เท่าที่พื้นที่จอมือถือรับไหว (วัดแล้วใบยังจบในจอเดียว) */
            sx={{ minHeight: { xs: 36, sm: 40 }, py: { xs: 0.25, sm: 0.5 }, fontWeight: 800 }}
          >
            {cancelling ? "กำลังยกเลิก..." : "ยกเลิกการจับคู่"}
          </Button>
        </>
      )}

      <Typography align="center" sx={{ fontSize: 12, opacity: 0.55 }}>
        ค่าธรรมเนียม 0% · ฝากขั้นต่ำ {minAmount} บาท
      </Typography>
      {/* cancelConfirm — ถามก่อนทิ้งใบ กันลูกค้าที่โอนไปแล้วกดพลาด */}
      <Dialog open={confirmCancel} onClose={() => setConfirmCancel(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>ยืนยันว่ายังไม่ได้โอน?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            ถ้าโอนเงินไปแล้ว <b>ห้ามกดยกเลิก</b> — ให้รอระบบจับคู่ยอด หรือแนบสลิปแทน
            กดยกเลิกแล้วรายการจะถูกปิด เงินที่โอนไปจะไม่ถูกจับคู่อัตโนมัติ
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button onClick={() => setConfirmCancel(false)} variant="contained" sx={{ fontWeight: 800, minHeight: 42 }}>
            ยังไม่ยกเลิก
          </Button>
          <Button
            color="error"
            onClick={() => {
              setConfirmCancel(false);
              void resetToForm();
            }}
            sx={{ fontWeight: 800, minHeight: 42 }}
          >
            ยืนยัน ยังไม่ได้โอน
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
